import {Controller} from '@hotwired/stimulus';

/**
 * Dépôt d'un fichier (file-upload.md, variante single) : une zone à filet pointillé qui accepte le glisser-déposer et
 * porte le bouton « Choisir un fichier », remplacée par la ligne du fichier une fois choisi, avec son bouton
 * « Retirer ». Le champ natif reste dans la zone, masqué et hors de l'ordre de tabulation : c'est lui qui est envoyé.
 * Le widget legacy `forms.fileInput` (`manager-forms.js`) laisse de côté un champ qui porte la target `input`.
 *
 *   <div class="file-upload" {{ stimulus_controller('file-upload', {dropLabel: 'cle'|trans}) }}>
 *     <div class="file-upload-zone" {{ stimulus_target('file-upload', 'zone') }}
 *          {{ stimulus_action('file-upload', 'over', 'dragover')
 *             |stimulus_action('file-upload', 'leave', 'dragleave')
 *             |stimulus_action('file-upload', 'drop', 'drop') }}>
 *       <span {{ stimulus_target('file-upload', 'prompt') }}>Glissez votre fichier ici ou</span>
 *       <button type="button" class="btn btn-ds-secondary"
 *               {{ stimulus_target('file-upload', 'browse') }}
 *               {{ stimulus_action('file-upload', 'browse') }}>…</button>
 *       <input type="file" class="visually-hidden" tabindex="-1"
 *              {{ stimulus_target('file-upload', 'input') }} {{ stimulus_action('file-upload', 'chosen', 'change') }}>
 *     </div>
 *     <div class="file-upload-item" hidden {{ stimulus_target('file-upload', 'item') }}>
 *       <span {{ stimulus_target('file-upload', 'name') }}></span>
 *       <span {{ stimulus_target('file-upload', 'size') }}></span>
 *       <button type="button" aria-label="Retirer le fichier"
 *               {{ stimulus_target('file-upload', 'remove') }} {{ stimulus_action('file-upload', 'clear') }}>…</button>
 *     </div>
 *   </div>
 *
 * Un fichier choisi, déposé ou retiré émet `file-upload:change`. Un fichier que l'`accept` du champ n'admet pas
 * (le sélecteur du système le laisse choisir, le dépôt ne le filtre pas) n'est pas retenu et émet `file-upload:reject`.
 */
export default class extends Controller {
  static targets = ['zone', 'prompt', 'browse', 'input', 'item', 'name', 'size', 'remove'];
  static values = {dropLabel: String};

  connect() {
    this.promptLabel = this.promptTarget.textContent;
    this.render();
  }

  browse() {
    this.inputTarget.click();
  }

  over(event) {
    if (!event.dataTransfer?.types.includes('Files')) {
      return;
    }

    event.preventDefault();
    event.dataTransfer.dropEffect = 'copy';
    this.zoneTarget.classList.add('is-dragover');
    this.promptTarget.textContent = this.dropLabelValue;
  }

  leave(event) {
    // un `dragleave` part aussi quand le pointeur passe sur un enfant de la zone ; Safari n'y donne pas de
    // `relatedTarget`, d'où la position du pointeur
    const zone = this.zoneTarget.getBoundingClientRect();
    const inside = event.clientX > zone.left && event.clientX < zone.right &&
      event.clientY > zone.top && event.clientY < zone.bottom;

    if (inside || this.zoneTarget.contains(event.relatedTarget)) {
      return;
    }

    this.endDrag();
  }

  drop(event) {
    if (!event.dataTransfer?.files.length) {
      return;
    }

    event.preventDefault();
    this.endDrag();

    // variante single : seul le premier fichier déposé est retenu
    const files = new DataTransfer();
    files.items.add(event.dataTransfer.files[0]);
    this.inputTarget.files = files.files;
    this.chosen();
  }

  chosen() {
    const chosen = this.inputTarget.files[0] ?? null;

    if (chosen !== null && !this.accepts(chosen)) {
      this.inputTarget.value = '';
      this.render();
      this.browseTarget.focus();
      this.dispatch('reject', {detail: {file: chosen}});

      return;
    }

    const file = this.render();

    // le bouton qui avait le focus disparaît avec la zone : le focus passe sur « Retirer »
    (file === null ? this.browseTarget : this.removeTarget).focus();
    this.dispatch('change', {detail: {file}});
  }

  clear() {
    this.inputTarget.value = '';
    this.render();
    this.browseTarget.focus();
    this.dispatch('change', {detail: {file: null}});
  }

  render() {
    const file = this.inputTarget.files[0] ?? null;

    this.zoneTarget.hidden = file !== null;
    this.itemTarget.hidden = file === null;

    if (file !== null) {
      this.nameTarget.textContent = file.name;
      this.sizeTarget.textContent = this.formatSize(file.size);
    }

    return file;
  }

  // même lecture que le sélecteur du système : extensions (`.csv`) et types (`text/csv`, `image/*`)
  accepts(file) {
    const rules = this.inputTarget.accept.split(',').map((rule) => rule.trim().toLowerCase()).filter(Boolean);
    const name = file.name.toLowerCase();
    const type = file.type.toLowerCase();

    return rules.length === 0 || rules.some((rule) => {
      if (rule.startsWith('.')) {
        return name.endsWith(rule);
      }

      return rule.endsWith('/*') ? type.startsWith(rule.slice(0, -1)) : type === rule;
    });
  }

  endDrag() {
    this.zoneTarget.classList.remove('is-dragover');
    this.promptTarget.textContent = this.promptLabel;
  }

  formatSize(bytes) {
    const [value, unit] = [[bytes, 'byte'], [bytes / 1024, 'kilobyte'], [bytes / (1024 * 1024), 'megabyte']]
      .find(([amount, name]) => amount < 1024 || name === 'megabyte');

    return new Intl.NumberFormat(document.documentElement.lang || undefined, {
      style: 'unit',
      unit,
      maximumFractionDigits: 1,
    }).format(value);
  }
}
